import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi, apiError } from "@/lib/api";
import { usePortal } from "@/portal/PortalContext";
import { Card, Empty } from "@/components/Shared";
import { ProductThumb } from "@/pages/QuoteBuilder";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Trash2, Plus, Minus, Loader2, Send } from "lucide-react";

export default function PortalCart() {
  const { cart, setQty, clearCart, refresh } = usePortal();
  const navigate = useNavigate();
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const subtotal = cart.reduce((s, l) => s + l.book_price * l.quantity, 0);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await portalApi.post("/portal/purchase-orders", { retailer_ref: ref || null, notes: notes || null, lines: cart.map((l) => ({ product_id: l.product_id, quantity: l.quantity })) });
      clearCart(); refresh(); toast.success(`${data.number} submitted to your distributor`); navigate(`/portal/orders/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (cart.length === 0) return <div data-testid="portal-cart-empty"><Empty icon={ShoppingCart} title="Your cart is empty" hint="Add products from the catalogue to raise a purchase order." /><div className="text-center"><Button className="bg-[#0B7C5A]" onClick={() => navigate("/portal")} data-testid="portal-cart-browse">Browse catalogue</Button></div></div>;
  return (
    <div data-testid="portal-cart">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#101D51] tracking-tight mb-4">Cart · {cart.length} item{cart.length > 1 ? "s" : ""}</h1>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 divide-y divide-[#F0F1F5]">
          {cart.map((l) => (
            <div key={l.product_id} className="p-3 flex items-center gap-3" data-testid={`cart-line-${l.code}`}>
              <ProductThumb url={l.image_url} size={12} />
              <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{l.name}</div><div className="text-[11px] text-[#6B7189]">{l.code} · {formatINR(l.book_price)} / {l.unit}</div></div>
              <div className="flex items-center rounded-lg border border-[#E6E8F0] overflow-hidden">
                <button className="w-8 h-8 flex items-center justify-center" onClick={() => setQty(l.product_id, l.quantity - 1)} data-testid={`cart-minus-${l.code}`}><Minus className="w-3.5 h-3.5" /></button>
                <Input type="number" min="1" value={l.quantity} onChange={(e) => setQty(l.product_id, Math.max(0, Number(e.target.value)))} className="w-14 h-8 text-center border-0 rounded-none px-1" data-testid={`cart-qty-${l.code}`} />
                <button className="w-8 h-8 flex items-center justify-center" onClick={() => setQty(l.product_id, l.quantity + 1)} data-testid={`cart-plus-${l.code}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="w-24 text-right text-sm font-semibold text-[#101D51]">{formatINR(l.book_price * l.quantity)}</div>
              <button onClick={() => setQty(l.product_id, 0)} className="text-[#6B7189] hover:text-[#F83A84]" data-testid={`cart-remove-${l.code}`}><Trash2 className="w-4 h-4" /></button>
            </div>))}
        </Card>
        <Card className="p-4 space-y-3 self-start">
          <div><label className="text-xs font-medium text-[#6B7189]">Your PO reference (optional)</label><Input value={ref} onChange={(e) => setRef(e.target.value)} className="h-10 mt-1" placeholder="e.g. MH/PO/2026-041" data-testid="cart-ref" /></div>
          <div><label className="text-xs font-medium text-[#6B7189]">Notes for distributor</label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="cart-notes" /></div>
          <div className="flex justify-between text-sm pt-2 border-t border-[#E6E8F0]"><span className="text-[#6B7189]">Subtotal (before GST)</span><span className="font-bold text-[#101D51]" data-testid="cart-subtotal">{formatINR(subtotal)}</span></div>
          <p className="text-[11px] text-[#6B7189]">GST is added by your distributor on the sales order and invoice.</p>
          <Button className="w-full h-11 bg-[#0B7C5A] hover:bg-[#09664a]" onClick={submit} disabled={saving} data-testid="cart-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit purchase order</>}</Button>
        </Card>
      </div>
    </div>
  );
}
